// TOTP Challenge — v0.7.x
//
// After a password sign-in, if the user has a verified TOTP factor and
// the JWT is still at aal1, Login.js routes them here. Two paths from
// this page:
//
//   1. TOTP code (normal happy path) — enter the 6 digits from the
//      authenticator app. `auth.mfa.challengeAndVerify` upgrades the
//      session to aal2 and we forward to the user's intended route.
//
//   2. Recovery code (lost-authenticator path) — enter one of the 10
//      backup codes issued at enrollment. The server-side function
//      validates the code, marks it consumed, deletes the TOTP factor,
//      and invalidates the remaining codes. The user is then taken to
//      Account → Security with a banner to re-enroll a fresh
//      authenticator. They stay signed in at aal1 because the factor
//      is gone — no more challenge is owed.

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

const pageStyle = {
  background: '#f3f4f6',
  minHeight: '100vh',
  padding: '60px 20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: '16px',
  padding: '36px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  width: '100%',
  maxWidth: '440px',
};

const heading = {
  fontSize: '28px',
  fontWeight: 800,
  color: '#111827',
  margin: '0 0 6px',
};

const subheading = {
  fontSize: '15px',
  color: '#6b7280',
  margin: '0 0 24px',
  lineHeight: 1.5,
};

const labelStyle = {
  display: 'block',
  fontWeight: 700,
  marginBottom: '8px',
  fontSize: '15px',
  color: '#111827',
};

const totpInputStyle = {
  width: '100%',
  padding: '14px',
  borderRadius: '12px',
  border: '1px solid #d1d5db',
  fontSize: '18px',
  letterSpacing: '3px',
  textAlign: 'center',
  fontWeight: 600,
  boxSizing: 'border-box',
  fontFamily: 'monospace',
  marginBottom: '18px',
};

const recoveryInputStyle = {
  ...totpInputStyle,
  letterSpacing: '2px',
  fontSize: '17px',
};

const buttonStyle = (enabled) => ({
  width: '100%',
  background: enabled ? '#1d4ed8' : '#9ca3af',
  color: '#ffffff',
  border: 'none',
  borderRadius: '12px',
  padding: '15px',
  fontSize: '17px',
  fontWeight: 700,
  cursor: enabled ? 'pointer' : 'not-allowed',
  marginBottom: '12px',
});

const secondaryButtonStyle = {
  width: '100%',
  background: '#f3f4f6',
  color: '#111827',
  border: '1px solid #d1d5db',
  borderRadius: '12px',
  padding: '15px',
  fontSize: '17px',
  fontWeight: 700,
  cursor: 'pointer',
};

const linkButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#1d4ed8',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '14px',
  padding: 0,
  marginTop: '4px',
  marginBottom: '20px',
};

const errorBox = {
  background: '#fff5f5',
  border: '1px solid #ef4444',
  borderRadius: '12px',
  padding: '14px',
  marginBottom: '20px',
  color: '#b91c1c',
  fontSize: '14px',
  lineHeight: 1.5,
};

const infoBox = {
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: '12px',
  padding: '14px',
  marginBottom: '20px',
  color: '#1e40af',
  fontSize: '13px',
  lineHeight: 1.6,
};

// Format a recovery code as the user types, so they can paste either
// "abc123def456" or "abc1-23de-f456" and get a consistent view.
function formatRecoveryCode(raw) {
  const cleaned = String(raw || '')
    .toLowerCase()
    .replace(/[^a-f0-9]/g, '')
    .slice(0, 12);
  const parts = [];
  if (cleaned.length > 0) parts.push(cleaned.slice(0, 4));
  if (cleaned.length > 4) parts.push(cleaned.slice(4, 8));
  if (cleaned.length > 8) parts.push(cleaned.slice(8, 12));
  return parts.join('-');
}

export default function TotpChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { consumeRecoveryCode } = useAuth();

  // 'totp' = enter 6-digit code from authenticator app
  // 'recovery' = enter one of the 10 backup codes
  const [mode, setMode] = useState('totp');

  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const redirectTo = location.state?.from?.pathname || '/dashboard';

  async function handleVerifyTotp(e) {
    e.preventDefault();

    if (!code || code.length !== 6 || !/^\d+$/.test(code)) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;

      if (!session) {
        setError('Session expired. Please sign in again.');
        setIsVerifying(false);
        navigate('/login');
        return;
      }

      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const allFactors =
        factorsData?.all ??
        factorsData?.factors ??
        [
          ...(factorsData?.totp ?? []),
          ...(factorsData?.phone ?? []),
        ];
      const totpFactor = allFactors.find(
        (f) => f?.factor_type === 'totp' && f?.status === 'verified'
      );

      if (!totpFactor) {
        setError(
          'No active authenticator was found on your account. Sign in again and finish setting one up in Account → Security.'
        );
        setIsVerifying(false);
        return;
      }

      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: totpFactor.id,
        code,
      });

      if (verifyError) {
        setError(verifyError.message || 'Invalid code. Please try again.');
        setIsVerifying(false);
        return;
      }

      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
      setIsVerifying(false);
    }
  }

  async function handleVerifyRecovery(e) {
    e.preventDefault();

    // Strip dashes and validate format — 12 hex chars (with or without
    // dashes when the user pasted/typed).
    const stripped = recoveryCode.replace(/[^a-f0-9]/gi, '');
    if (!/^[a-f0-9]{12}$/i.test(stripped)) {
      setError('Recovery codes are 12 letters/digits (dashes optional).');
      return;
    }

    setIsVerifying(true);
    setError('');

    const { error: consumeError } = await consumeRecoveryCode(stripped);

    if (consumeError) {
      setIsVerifying(false);
      setError(
        consumeError.message ||
          "That recovery code didn't match. Check the dashes and try again."
      );
      return;
    }

    // Code accepted → server-side, the TOTP factor was deleted and the
    // remaining codes invalidated. The session stays as-is (aal1) but
    // since there's no longer an enrolled factor, the challenge gate
    // won't fire again. Navigate to Account → Security with a banner
    // prompting the user to enroll a new authenticator.
    navigate('/account', {
      replace: true,
      state: {
        recoveryUsed: true,
        recoveryNotice:
          'Recovery code accepted. Your previous authenticator has been removed — set up a new one below to keep 2FA on.',
      },
    });
  }

  function handleSignOut() {
    supabase.auth.signOut().then(() => {
      navigate('/login', { replace: true });
    });
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={heading}>Verify your identity</h1>
        <p style={subheading}>
          {mode === 'totp'
            ? 'Enter the 6-digit code from your authenticator app.'
            : 'Enter one of the recovery codes you saved when you set up 2FA.'}
        </p>

        {error && <div style={errorBox}>{error}</div>}

        {mode === 'totp' && (
          <>
            <div style={infoBox}>
              <strong>Tip:</strong> Open Google Authenticator, 1Password, Authy, or your other
              authenticator app and copy the 6-digit code.
            </div>

            <form onSubmit={handleVerifyTotp}>
              <label htmlFor="totp-code" style={labelStyle}>
                Authenticator code
              </label>
              <input
                id="totp-code"
                type="text"
                inputMode="numeric"
                maxLength="6"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={totpInputStyle}
                disabled={isVerifying}
                autoFocus
                autoComplete="one-time-code"
              />

              <button
                type="submit"
                disabled={code.length !== 6 || isVerifying}
                style={buttonStyle(code.length === 6 && !isVerifying)}
              >
                {isVerifying ? 'Verifying…' : 'Verify code'}
              </button>
            </form>

            <button
              type="button"
              onClick={() => {
                setError('');
                setMode('recovery');
              }}
              style={linkButtonStyle}
            >
              Lost your authenticator? Use a recovery code →
            </button>
          </>
        )}

        {mode === 'recovery' && (
          <>
            <div style={infoBox}>
              <strong>Heads up:</strong> using a recovery code is a one-time deal. It will
              remove your current authenticator and invalidate your other recovery codes —
              you'll set up a new authenticator after.
            </div>

            <form onSubmit={handleVerifyRecovery}>
              <label htmlFor="recovery-code" style={labelStyle}>
                Recovery code
              </label>
              <input
                id="recovery-code"
                type="text"
                placeholder="xxxx-xxxx-xxxx"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(formatRecoveryCode(e.target.value))}
                style={recoveryInputStyle}
                disabled={isVerifying}
                autoFocus
                autoComplete="off"
                spellCheck={false}
              />

              <button
                type="submit"
                disabled={
                  recoveryCode.replace(/[^a-f0-9]/gi, '').length !== 12 || isVerifying
                }
                style={buttonStyle(
                  recoveryCode.replace(/[^a-f0-9]/gi, '').length === 12 && !isVerifying
                )}
              >
                {isVerifying ? 'Checking…' : 'Use recovery code'}
              </button>
            </form>

            <button
              type="button"
              onClick={() => {
                setError('');
                setMode('totp');
              }}
              style={linkButtonStyle}
            >
              ← Back to authenticator code
            </button>
          </>
        )}

        <button type="button" onClick={handleSignOut} style={secondaryButtonStyle}>
          Sign in with a different account
        </button>
      </div>
    </div>
  );
}
